import React, { memo, useEffect, useRef } from 'react'
import { useRecurly } from '@recurly/react-recurly'
import { ApplePayInstance, RecurlyError } from '@recurly/recurly-js'
import { useSubscribeAnalyticsEvent } from '../../../hooks/use-subscribe-analytics-event.web'
import { PaymentMethods, SubscriptionPlans } from '@pff-consumer/schema'
import { checkoutHooks } from '@pff-consumer/data-provider/lib/checkout/checkout.hooks'
import { SVGGroup } from '@pff-consumer/utils/lib/svg/svg-group'
import { Button } from '../../button/button.web'
import { ApplePayPaymentProps } from './apple-pay-payment.props'
import { appleIconProps } from '../../../shared/payment'

const { useEmail } = checkoutHooks

export const ApplePayPayment = memo((props: ApplePayPaymentProps) => {
  const {
    isDisabled,
    isOnCompletePending,
    onComplete,
    onDisabledClick,
    onError,
    plan,
    price,
    onRecurlyTokenInit,
    isPaymentProcessing,
    size,
  } = props
  const applePayRef = useRef<ApplePayInstance | null>(null)
  const { sendSubscribeAnalyticsEvent } = useSubscribeAnalyticsEvent()
  const onCompleteRef = useRef(onComplete)
  const recurly = useRecurly()
  const email = useEmail()

  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  useEffect(() => {
    // These labels are prefixed with "Pay " by recurly-js
    const monthlyPayLabel = `${price.currency.symbol}${price.next.total} per month`
    const annualPayLabel = `${price.currency.symbol}${price.next.total} billed annually`

    const label =
      plan === SubscriptionPlans.PREMIUM_MONTHLY_2024_DRAFT_EARLY ||
      plan === SubscriptionPlans.PREMIUM_MONTHLY_2024_DRAFT_PEAK ||
      plan === SubscriptionPlans.PREMIUM_MONTHLY_SEASON_PASS
        ? monthlyPayLabel
        : annualPayLabel
    const total = price.now.total!

    const applePay = recurly.ApplePay({
      country: 'US',
      currency: 'USD',
      label,
      total,
      braintree: { clientAuthorization: process.env.BRAINTREE_CLIENT_KEY! },
    })

    applePay.on('token', (token: { id: string }) => {
      // the applePay token event listener can register the callback before the onComplete updates,
      // so the ref lets us use the onComplete callback when its called, not when its defined.
      onCompleteRef.current(token.id, 'apple_pay', plan)
    })

    applePay.on('error', (error: RecurlyError) => {
      console.log('error ', error)
      onError(error)
    })

    applePay.on('ready', () => {
      applePayRef.current = applePay
    })

    applePay.on('cancel', () => {
      // In rare cases ApplePay will cancel the transaction before it starts with an error in the params of this handler
      // Instead of returning the error in the error handler it will only log on the params of this handler and fail silently
      // We may have to handle these cancel error cases as they pop up in the future
      // params?.sessionError?.code === 'unknown' is not enough to handle the errors since even valid cancels have this error code
      onRecurlyTokenInit(PaymentMethods.DEFAULT)
      sendSubscribeAnalyticsEvent('Transaction Canceled', {
        payment_method: 'apple_pay',
        email,
      })
    })
    // We need to exclude onComplete, onError, and recurly because they do not remain stable between re-renders
    // If we include them we will consistently see an error on iOS Safari when pull down to refresh occurs due to this useEffect
    // needlessly re-triggering and then the page quickly refreshing
  }, [
    plan,
    price.currency.symbol,
    price.next.total,
    price.now.total,
    sendSubscribeAnalyticsEvent,
    email,
    onRecurlyTokenInit,
    recurly,
    onError,
  ])

  const handleClick = () => {
    if (!isPaymentProcessing) {
      onRecurlyTokenInit(PaymentMethods.APPLE_PAY)
    }
    if (applePayRef && applePayRef.current) {
      applePayRef.current.begin()
    }
    sendSubscribeAnalyticsEvent('CTA Clicked', {
      ctaName: 'Apple Pay',
      ctaLocation: 'Single Page Checkout',
    })
  }

  return (
    <Button
      variant='Apple'
      iconGroup={SVGGroup.Icons}
      isPending={isPaymentProcessing}
      onClick={isDisabled ? onDisabledClick : handleClick}
      data-analytics-id='apple-pay-button'
      disabled={isOnCompletePending}
      size={size}
      {...(size === 'sm' && appleIconProps)}
    />
  )
})
